import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Notification } from 'fave-ui';
import { Col, Row } from 'antd';
import { Form, Input, SubmitButton } from '../../components/common/form';
import { useManualRefundMutation } from '../../services/ManualRefund/useFetchFormFields';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

const successNotification = () => {
  Notification.success({
    message: 'Refund Submitted!',
  });
};

type RenderRefundFormProps = {
  paymentType: string;
  fields: {
    identifier: string;
    name: string;
    optional: boolean;
    type: string;
  }[];
};

const RenderRefundForm = ({ paymentType, fields }: RenderRefundFormProps) => {
  const navigate = useNavigate();
  const { mutate: manualRefund } = useManualRefundMutation();

  const handleSubmit = async (
    values: Record<string, string>,
    { setSubmitting, resetForm }: FormikHelpers<Record<string, string>>,
  ) => {
    setSubmitting(true);

    const valueKeys = Object.keys(values);

    // If the field is optional include it inside the `payload.details` object (except for remarks field)
    const payload = fields.reduce<{ details?: Record<string, string> }>(
      (acc, field) => {
        if (
          values[field.identifier] === '' ||
          values[field.identifier] === undefined ||
          values[field.identifier] === null
        )
          return acc;

        if (
          field.optional &&
          field.identifier !== 'remarks' &&
          valueKeys.includes(field.identifier)
        ) {
          return {
            ...acc,
            details: {
              ...acc.details,
              [field.identifier]: values[field.identifier],
            },
          };
        } else {
          return {
            ...acc,
            [field.identifier]: values[field.identifier],
          };
        }
      },
      {},
    );

    try {
      manualRefund(
        {
          payment_type: paymentType,
          ...payload,
        },
        {
          onSuccess: () => {
            setSubmitting(false);
            successNotification();
            resetForm();

            // Redirect to maker checker dashboard
            navigate('/admin/maker_checker_dashboard');
          },
        },
      );
    } finally {
      setSubmitting(false);
    }
  };

  const RenderRefundFormSchema = useMemo(() => {
    return Yup.object().shape(
      fields.reduce<Record<string, Yup.AnySchema>>((acc, field) => {
        acc[field.identifier] = field.optional
          ? Yup.string()
          : Yup.string().required(`${field.name} is required`);
        return acc;
      }, {}),
    );
  }, [fields]);

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{}}
      validationSchema={RenderRefundFormSchema}
      validateOnMount
      validateOnChange
      validateOnSchemaChange
    >
      <Row gutter={[16, 16]}>
        {fields.map(field => (
          <Col span={24} key={field.identifier}>
            <Input
              name={field.identifier}
              label={field.name}
              type={field.type}
              placeholder={field.name}
              allowClear
              bordered
              style={{ marginBottom: 0 }}
            />
          </Col>
        ))}

        <Col span={24}>
          <SubmitButton disableIfNotValid disableIfNotDirty>
            {(_, isSubmitting) => (isSubmitting ? 'Submitting...' : 'Submit')}
          </SubmitButton>
        </Col>
      </Row>
    </Form>
  );
};

export default RenderRefundForm;
